import {Page} from "../page"

export const notifications: Page = {
    uri: 'learn/notifications',
    icon: '🔔',
    title: $localize`:@@page-notifications-title:Notifications`,
    seoTitle: $localize`:@@page-notifications-seo-title:Notification settings — channels, digests and quiet control`,
    summary: $localize`:@@page-notifications-summary:One inbox for your plays, events, community and deals: grouped, filterable, with the common answers one tap away. You choose, topic by topic, whether each also reaches your phone or your e-mail.`,
    markdownContent: [
        $localize`:@@page-notifications-the-notification-system:Stay connected with real-time updates from your gaming community. Ludoya notifies you about plays, events, groups, marketplace activity, shop deals, and more — and you decide how each one reaches you.

## Your Inbox

Tap the **bell** — in the bottom bar on mobile, or the side menu on desktop — to open your notifications, with an unread badge showing how many are waiting. Everything syncs across your devices, so reading a notification on your phone clears it on your laptop too.

![An organizer's inbox: sign-ups grouped per event, and new game nights nearby](/assets/screenshots/notifications-list.webp)

- **Sorted by day**: today, yesterday, and earlier, newest first.
- **Grouped when it piles up.** Eight people joining your event is one line, "Vera Lindqvist and 7 others joined your event", not eight.
- **Each row says where it comes from**: the event, the group, or the town a new event is in, with the game's cover beside it.
- **Filters** at the top narrow the list to **Mentions**, **Events** or **Marketplace**.`,

        $localize`:@@page-notifications-answer-from-the-inbox:## Answer Without Leaving the List

The common replies are buttons on the notification itself:

- **Approve** or **Decline** a request to join your event or group
- **Follow back** someone who just followed you
- **Reply** to a comment, right there in the list

Every notification also has **Mark as read** and **Delete notification**, and the double tick at the top marks everything read at once.`,

        $localize`:@@page-notifications-delivery-channels:## Delivery Channels

![Notification settings: in app, push and e-mail columns for every topic](/assets/screenshots/notifications-settings.webp)

In **Settings → Notifications**, every kind of notification has three columns: **In app**, **Push** and **E-mail**. They build on each other: a notification that reaches your e-mail also pushes to your phone and shows in the app, and one that's only in the app stays quiet everywhere else. Untick **In app** to turn a topic off entirely.

Each row says what triggers it, each section can be collapsed, and **Turn all off** or **Turn all on** handles a whole section at once. Press **Save** when you're done.

Nothing is all-or-nothing: you can have event reminders pushed to your phone, marketplace inquiries e-mailed, and likes stay quietly in-app.

**Send me e-mails** at the top is the master switch. With it off, nothing reaches your e-mail inbox; anything set to e-mail still arrives as a push notification.

Only use Ludoya in the browser, or never installed the app? Then there's no phone to push to, so what's set to **Push** comes by e-mail instead (unless you're on Ludoya at that moment, or **Send me e-mails** is off). It goes back to push as soon as you sign in on the app and allow its notifications. Likes, badges and shop deals stay in the app either way.`,

        $localize`:@@page-notifications-what-you-can-be-notified-about:## What You Can Be Notified About

Settings are grouped the same way the app groups them.

### Social
When you're tagged in a play, when someone follows you, when you're mentioned in a post or comment, when someone likes or comments on your posts, reviews or plays, and when someone asks to [share a collection](/learn/collection) with you.

### Messages
Someone writes to you in a [conversation](/learn/messaging). Your device is pinged whenever you're away; the e-mail only follows if you stay away. Each group chat also has its own level (every message, mentions and replies, or muted) in its info panel.

### Events
When an event is organized nearby, event interactions (someone joins or leaves an [event](/learn/events) of yours, or a game is scheduled inside one you attend), announcements from the organizers of an event you're part of, reminders 24 hours and 30 minutes before it starts, [raffles](/learn/events) you can enter or win, the club's game library (a game you waited for comes free, or one you borrowed is due back), and your table bookings at a venue.

### Groups
When a [group](/learn/groups) is created near you, group interactions (join requests, approvals) in the communities you belong to, **new club events** (a club you belong to publishes an event or schedules a game), and announcements a club you belong to sends its members.

Until you set **new club events** yourself, it follows what you chose for event interactions. A run of dates with the same name that a club or an organizer enters within a day (a weekly night, one date at a time) is announced once, both to its members and to people nearby.

### Marketplace
When someone asks about your listing, listing status changes (reserved, delivered, completed), listing expiration (about to expire, or expired), your boxes at an event's market (one sells, or the organizers ask you to collect), **wishlist matches** when a listing appears for a game you want, and transaction ratings. See [Marketplace](/learn/marketplace).

### Campaigns
Invites and join requests for your [campaigns](/learn/campaigns).

### Shops
**Price drops** and **new arrivals** from retail shops — see [Shops & Prices](/learn/shops-prices).

### Other
Your monthly plays summary, a weekly shelf reminder (the game on your shelf that has waited longest for a play), [badge unlocks](/learn/replay-badges), completed quest cards, the outcome of game catalog edits you submitted, BoardGameGeek link results, and BoardGameGeek sync problems.

Some closely-related notifications share one setting — "listing expiration" covers expiring and expired together, for example — so the settings screen stays short instead of listing thirty separate switches.

BoardGameGeek sync problems and **Announcements** from the Ludoya team can't be switched off, only moved between push and e-mail. Billing notices are always delivered.`,

        $localize`:@@page-notifications-nearby-radius:## Radius for Nearby Alerts

"Nearby event" and "nearby group" alerts use your profile location, and you choose how far out to listen. The two distances sit right under the nearby-events row in **Settings → Notifications**:

- **Any event**: how far away an event can be and still reach you. This distance also covers nearby groups and local-only marketplace matches. The default is 5 km.
- **Games you want to play**: a separate, usually wider distance for events with a game on your [want-to-play list](/learn/collection) or wishlist. Worth a longer trip, so it defaults to 30 km.

Each offers 2 km (your neighborhood), 5 km, 30 km (regional) and 100 km (wide area).`,

        $localize`:@@page-notifications-managing-your-notifications:## Managing Your Notifications

**Tap to open:** a notification takes you to whatever it's about, and it's marked read on the way.

**Change your mind anytime:** the gear at the top of the inbox opens your settings, so it's easy to dial things down during a busy week and turn them back up later.`,

        $localize`:@@page-notifications-related:## Related

- [Privacy & Visibility](/learn/privacy-visibility) — your location and who can contact you
- [Events](/learn/events) — create and attend gaming events
- [Marketplace](/learn/marketplace) — buy, sell, and trade games
- [Shops & Prices](/learn/shops-prices) — where price-drop alerts come from
- [Social Feed](/learn/social-feeds) — follow players and browse activity`,
    ].join('\n\n---\n\n')
}
